import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";
import { onConsentChange, readConsent } from "@/lib/cookieConsent";
import { matchSupportTopic, type SupportTopic } from "@/lib/supportChat";

const SUGGESTIONS = ["hours", "refund", "payment", "human"] as const;
const MESSAGE_LIMIT = 500;

interface Bubble {
  id: number;
  role: "bot" | "user";
  text: string;
}

/**
 * Support chat on every page. It answers from the site's own policies and,
 * when a person is needed, sends the message to the same inbox as the contact form.
 */
export default function SupportChat() {
  const { t } = useTranslation();
  const titleId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const nextId = useRef(1);
  const lastQuestion = useRef("");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<Bubble[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);
  const [cookieBanner, setCookieBanner] = useState(() => readConsent() === null);

  useEffect(() => onConsentChange(() => setCookieBanner(readConsent() === null)), []);

  const bubble = (role: Bubble["role"], text: string): Bubble => {
    const id = nextId.current;
    nextId.current += 1;
    return { id, role, text };
  };

  const openChat = () => {
    setOpen(true);
    setMessages((current) => (current.length ? current : [bubble("bot", t("supportChat.greeting"))]));
  };

  const reply = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed) return;
    const topic: SupportTopic | null = matchSupportTopic(trimmed);
    const answer = topic ? t(`supportChat.answers.${topic}`) : t("supportChat.fallback");
    lastQuestion.current = trimmed;
    const userBubble = bubble("user", trimmed);
    const botBubble = bubble("bot", answer);
    setMessages((current) => [...current, userBubble, botBubble]);
    setDraft("");
    if (topic === "human") {
      setNote((current) => current || trimmed);
      setFormOpen(true);
    }
  };

  const ask = (topic: (typeof SUGGESTIONS)[number]) => {
    reply(t(`supportChat.topics.${topic}`));
  };

  const sendNote = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedNote = note.trim();
    if (!trimmedName) {
      setFormError(t("supportChat.form.nameRequired"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setFormError(t("supportChat.form.emailRequired"));
      return;
    }
    if (!trimmedNote) {
      setFormError(t("supportChat.form.messageRequired"));
      return;
    }

    setSending(true);
    setFormError("");
    try {
      await api.post("/contact", {
        name: trimmedName,
        email: trimmedEmail,
        subject: t("supportChat.form.subject"),
        message: trimmedNote.slice(0, MESSAGE_LIMIT),
        website_alt: "",
      });
      setMessages((current) => [...current, bubble("bot", t("supportChat.form.success"))]);
      setFormOpen(false);
      setNote("");
    } catch (err) {
      setFormError(
        err instanceof ApiError && err.status !== 0 ? err.message : t("home.contact.networkError")
      );
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, formOpen, open]);

  const fieldClass =
    "w-full rounded-lg border border-background-300/70 bg-background-50 px-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400";

  return (
    <div className="print:hidden">
      {open && (
        <section
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          className="fixed bottom-4 right-4 z-[65] flex w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 shadow-2xl"
          style={{ height: "min(34rem, calc(100dvh - 2rem))" }}
        >
          <header className="flex items-start gap-3 bg-primary-950 px-4 py-3 text-background-50">
            <i className="ri-customer-service-2-line mt-0.5 text-xl text-accent-200" aria-hidden="true"></i>
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-sm font-semibold">
                {t("supportChat.title")}
              </h2>
              <p className="mt-0.5 text-xs leading-relaxed text-background-50/70">{t("supportChat.subtitle")}</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("supportChat.close")}
              className="rounded-full p-1 text-background-50/80 hover:text-background-50 cursor-pointer"
            >
              <i className="ri-close-line text-xl" aria-hidden="true"></i>
            </button>
          </header>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite">
            {messages.map((message) => (
              <p
                key={message.id}
                className={`max-w-[90%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                  message.role === "user"
                    ? "ml-auto bg-primary-600 text-background-50"
                    : "bg-background-100 text-foreground-800"
                }`}
              >
                {message.text}
              </p>
            ))}
          </div>

          {formOpen ? (
            <form onSubmit={(e) => void sendNote(e)} className="space-y-2 border-t border-background-200/70 p-3">
              <label className="block text-xs font-medium text-foreground-700">
                {t("supportChat.form.name")}
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  className={`${fieldClass} mt-1`}
                />
              </label>
              <label className="block text-xs font-medium text-foreground-700">
                {t("supportChat.form.email")}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className={`${fieldClass} mt-1`}
                />
              </label>
              <label className="block text-xs font-medium text-foreground-700">
                {t("supportChat.form.message")}
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, MESSAGE_LIMIT))}
                  required
                  rows={3}
                  className={`${fieldClass} mt-1 resize-none`}
                />
              </label>
              <p className="text-xs text-foreground-400">{t("supportChat.form.messageHint")}</p>
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
              {formError && <p className="text-xs text-red-600">{formError}</p>}
              <button
                type="submit"
                disabled={sending}
                className="w-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500 py-2.5 text-sm font-semibold text-background-50 disabled:opacity-60 cursor-pointer"
              >
                {sending ? t("supportChat.form.sending") : t("supportChat.form.send")}
              </button>
            </form>
          ) : (
            <div className="border-t border-background-200/70 p-3">
              <div className="mb-2 flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((topic) => (
                  <button
                    key={topic}
                    type="button"
                    onClick={() => ask(topic)}
                    className="rounded-full border border-background-300/70 px-2.5 py-1 text-xs font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                  >
                    {t(`supportChat.topics.${topic}`)}
                  </button>
                ))}
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  reply(draft);
                }}
                className="flex gap-2"
              >
                <label className="sr-only" htmlFor="support-chat-draft">
                  {t("supportChat.placeholder")}
                </label>
                <input
                  id="support-chat-draft"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={t("supportChat.placeholder")}
                  className={fieldClass}
                />
                <button
                  type="submit"
                  disabled={!draft.trim()}
                  className="shrink-0 rounded-full bg-primary-600 px-3 text-sm font-semibold text-background-50 disabled:opacity-50 cursor-pointer"
                >
                  {t("supportChat.send")}
                </button>
              </form>
              <button
                type="button"
                onClick={() => {
                  setFormOpen(true);
                  setNote((current) => current || draft || lastQuestion.current);
                }}
                className="mt-2 text-xs font-semibold text-primary-600 hover:underline cursor-pointer"
              >
                {t("supportChat.leaveMessage")}
              </button>
            </div>
          )}
        </section>
      )}

      {!open && (
        <button
          type="button"
          onClick={openChat}
          className={`fixed right-4 z-[65] flex items-center gap-2 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-4 py-3 text-sm font-semibold text-background-50 shadow-lg cursor-pointer ${
            cookieBanner ? "bottom-52 sm:bottom-4" : "bottom-4"
          }`}
        >
          <i className="ri-customer-service-2-line text-lg" aria-hidden="true"></i>
          {t("supportChat.open")}
        </button>
      )}
    </div>
  );
}
